/**
 * Hook to fetch template media from API
 * Used by BaseTemplate to get dynamic media (videos and images)
 * 
 * IMPORTANT: Storage in PostgreSQL
 * - Videos: Only playback_id is stored, src is generated as Mux thumbnail
 * - Photos: Only src (full URL) is stored
 * 
 * Returns data in BaseTemplate format with automatic transformations
 */

import { useEffect, useState } from 'react';
import { transformApiMediaToBaseTemplate, type BaseTemplateMediaItem } from '@/lib/templateMediaTransform';

interface ApiTemplateMedia {
  id: number;
  type: 'video' | 'photo';
  src?: string;
  playback_id?: string;
  title: string;
  description?: string;
  action_button_url?: string;
  action_button_text?: string;
  is_active: boolean;
  display_order: number;
  template_name?: string;
  country_code?: string;
  created_at: string;
  updated_at: string;
}

interface UseTemplateMediaOptions {
  template_name?: string;
  country_code?: string;
  type?: 'video' | 'photo';
  is_active?: boolean;
}

interface UseTemplateMediaResult {
  media: BaseTemplateMediaItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Get API base URL
 */
function getApiBaseUrl(): string {
  // Check if we have a custom API URL
  const customApiUrl = import.meta.env.VITE_API_URL;
  if (customApiUrl) {
    return customApiUrl;
  }
  
  // Use current origin for API calls
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  
  return '';
}

/**
 * Hook to fetch template media
 * Pass null as options to skip API call (when feature flag is disabled)
 */
export function useTemplateMedia(options: UseTemplateMediaOptions | null = {}): UseTemplateMediaResult {
  const [media, setMedia] = useState<BaseTemplateMediaItem[]>([]);
  const [loading, setLoading] = useState(options !== null);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    // Skip API call if options is null (feature flag disabled)
    if (options === null) {
      setLoading(false);
      setMedia([]);
      setError(null);
      return;
    }

    const fetchMedia = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiBaseUrl = getApiBaseUrl();
        const queryParams = new URLSearchParams();

        if (options.template_name) {
          queryParams.append('template_name', options.template_name);
        }

        if (options.country_code) {
          queryParams.append('country_code', options.country_code);
        }

        if (options.type) {
          queryParams.append('type', options.type);
        }

        if (options.is_active !== undefined) {
          queryParams.append('is_active', String(options.is_active));
        }

        const url = `${apiBaseUrl}/api/template-media?${queryParams.toString()}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch media: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && Array.isArray(data.media)) {
          // Transform API media to BaseTemplate format
          const transformedMedia = transformApiMediaToBaseTemplate(data.media);
          setMedia(transformedMedia);
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        console.error('[useTemplateMedia] Error fetching media:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setMedia([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [
    options?.template_name,
    options?.country_code,
    options?.type,
    options?.is_active,
    refetchTrigger
  ]);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  return {
    media,
    loading,
    error,
    refetch
  };
}
